import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { email, required, sameAs } from 'vuelidate/lib/validators';

import { Popper } from '@monjin/vue-toolkit';

import { checkEmail } from '@api/employerRegistration';
import { matchError } from '@api/util';
import { OrganizationRegistration } from '@model/employer';
import { hasNumber, hasSpecialChars, hasUppercase, isPrivateEmail } from '../../../util/index';
import WithRender from './RegistrationForm.html';

interface PasswordHint {
    text: string;
    satisfied: boolean;
    type: string;
}

const PASSWORD_HINTS: PasswordHint[] = [
    {
        text: '8 - 20 characters',
        satisfied: false,
        type: 'lengthCheck'
    },
    {
        text: 'At least 1 uppercase character',
        satisfied: false,
        type: 'upperCaseCheck'
    },
    {
        text: 'At least 1 number',
        satisfied: false,
        type: 'numberCheck'
    },
    {
        text: 'At least 1 special character',
        satisfied: false,
        type: 'specialCharCheck'
    }
];
@WithRender
@Component({
    validations: {
        email: { required, email, isPrivateEmail },
        password: { required },
        password2: { sameAs: sameAs('password') }
    }
})
export default class RegistrationForm extends Vue {

    @Prop()
    public orgData: OrganizationRegistration;

    public $v: any;
    public $refs: {
        popper: Popper;
    };

    public errorMessage: string = '';

    public unknownError: boolean = false;

    public loading: boolean = false;

    public checking: boolean = false;

    public checkDone: boolean = false;

    public ifEmailExists: boolean = false;

    // Form model
    public email = '';
    public password = '';

    // One additional field
    public password2 = '';

    public isRequestInProgress = false;

    public passwordFieldType: string = 'password';
    public confirmPasswordFieldType: string = 'password';

    public passwordCriterionSatisfied = false;

    public passwordHints: PasswordHint[] = PASSWORD_HINTS;

    public created() {
        this.email = this.orgData.adminEmail;
        this.password = this.orgData.password;
    }

    public hasError(model: string) {
        return this.$v.$dirty && this.$v[model].$error;
    }

    public disablePaste(e) {
        e.preventDefault();
    }

    public onSubmit() {
        // Dirty all inputs
        this.$v.$touch();

        if (!this.checkPasswordStrength() || this.$v.$invalid || this.ifEmailExists) {
            return;
        }

        this.isRequestInProgress = true;

        const firstFormData = {
            email: this.email,
            password: this.password
        };

        this.$emit('next', 1, firstFormData);
    }

    public switchVisiblity(fieldName: string) {
        if (fieldName === 'password') {
            this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
        } else {
            this.confirmPasswordFieldType = this.confirmPasswordFieldType === 'password' ? 'text' : 'password';
        }
    }

    public checkPasswordStrength(showPoper: boolean = true): boolean {

        this.passwordHints.map((x) => {
            x.satisfied = false;
        });

        if (this.password.length >= 8 && this.password.length <= 20) {
            this.setPasswordSatisfied('lengthCheck');
        }
        if (hasUppercase(this.password)) {
            this.setPasswordSatisfied('upperCaseCheck');
        }
        if (hasNumber(this.password)) {
            this.setPasswordSatisfied('numberCheck');
        }
        if (hasSpecialChars(this.password)) {
            this.setPasswordSatisfied('specialCharCheck');
        }

        const satisfied = this.isPasswordCriterionSatisfied();

        if (!satisfied && showPoper) {
            this.$refs.popper.show();
        }

        return satisfied;
    }

    private isPasswordCriterionSatisfied() {
        return this.passwordHints.every((x) => x.satisfied);
    }

    private setPasswordSatisfied(type: string) {
        this.passwordHints.map((x) => {
            if (x.type === type) {
                x.satisfied = true;
            }
        });
    }

    public checkEmailAvailability() {
        this.checkDone = false;

        if (this.email.trim().length) {

            if (!this.$v.email.email) {
                return;
            }

            this.checking = true;

            checkEmail(this.email)
                .then((response) => {
                    this.checking = false;
                    this.ifEmailExists = response;
                })
                .catch(matchError({
                    anyError: () => {
                        const message = 'Unable to verify email currently. Please try after some time.';

                        this.checking = false;
                        this.errorMessage = message;
                        this.unknownError = true;
                        this.$snackbar.error({ message });
                    }
                }))
                .then((x) => {
                    this.checking = false;
                    this.checkDone = true;
                });
        }
    }
}
